import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useSignalR } from "./useSignalR";
import { useSignalRConnections } from "../context/SignalRProvider";
import { setResourceDeleted } from "../redux/resourceDeletedSlice";
import { GLOBALSETTINGS } from "../data/constants";

interface ResourceDeletedPayload {
  folderId: number | null;
  fileId: number | null;
  parentFolderId: number | null;
}

/**
 * Bridges cross-session SignalR resource events into Redux so the file-explorer
 * grid stays in sync across browsers/tabs. Without this, a folder/file deleted
 * elsewhere stays visible until a manual refresh, and acting on the stale row
 * (Copy/Edit) errors out.
 *
 * A deletion soft-archives the resource for everyone, so we remove it for ANY
 * user who currently has it in their grid - not just the deleting user. The
 * backend sends `ResourceDeleted` as a global broadcast (Clients.All), and the
 * grid filters by the (globally unique) folder/file id, so a client that doesn't
 * have the row simply no-ops; the id match itself acts as the access check (you
 * only have the row if you had access to it). The event carries no recipient id
 * by design - do not reintroduce client-side user filtering here unless the hub
 * is also changed to a scoped/targeted send.
 */
export const useResourceSync = () => {
  const dispatch = useDispatch();
  const { notificationConnection } = useSignalRConnections();

  const handleResourceDeleted = useCallback(
    (payload: ResourceDeletedPayload) => {
      // Guard against malformed/partial/stringified SignalR payloads.
      if (!payload || typeof payload !== "object") return;
      dispatch(
        setResourceDeleted({
          folderId: payload.folderId ?? GLOBALSETTINGS.DEFAULT_STATE,
          parentFolderId: payload.parentFolderId ?? null,
          fileId: payload.fileId ?? GLOBALSETTINGS.DEFAULT_STATE,
        })
      );
    },
    [dispatch]
  );

  useSignalR(notificationConnection, "ResourceDeleted", handleResourceDeleted);
};
